import { create } from "zustand";
import { PdfHighlighterEntity, PdfPinEntity, PointsEntity, SystemDDLEntityForHighlights } from "../interface/pdf-pin";




interface usePDFPinStoreProps {
    systemDDL: SystemDDLEntityForHighlights[] | [];
    width: number;
    setSystemDDL: (systems: SystemDDLEntityForHighlights[]) => void;
    isHighlightsActive: boolean;
    isLinkActive: boolean;
    isMoveActive: boolean;

    highlights: PdfHighlighterEntity[] | [];
    selectedHighlight: string;
    highlightDataSet: MapEntity;

    setWidth: (width: number) => void;
    setHighlights: (highlight: PdfHighlighterEntity[]) => void;
    addHighlight: (highlight: PdfHighlighterEntity) => void;
    setIsHighlightsActive: (isActive: boolean) => void;
    setIsLinkActive: (isActive: boolean) => void;
    setIsMoveActive: (isActive: boolean) => void;
    updateHighlight: (id: string, highlight: Partial<PdfHighlighterEntity>) => void;
    deleteHighlight: (id: string) => void;


    selectedPageId: string;
    setSelectedPageId: (id: string) => void;

    addHighlightToPage: (pageId: string, highlight: PdfHighlighterEntity) => void;
    updateHighlightInPage: (pageId: string, highlightId: string, data: Partial<PdfHighlighterEntity>) => void;
    removeHighlightFromPage: (pageId: string, highlightId: string) => void;
    getHighlightsByPage: (pageId: string) => PdfHighlighterEntity[];

    setSelectedHighlight: (id: string) => void;


    // catch icon 
    iconCache: Map<string, HTMLImageElement>;
    setIconCache: (key: string, icon: HTMLImageElement) => void;
    getIconCache: (key: string) => HTMLImageElement | undefined;
    removeIconCache: (key: string) => void;
    clearIconCache: () => void;
    clearAllState: () => void;


    setHighlightDataSet: (data: MapEntity) => void;


}

export type MapEntity = Record<string, PdfHighlighterEntity[]>;

export const usePDFAnnotationStore = create<usePDFPinStoreProps>((set, get) => ({
    systemDDL: [],
    highlights: [],
    selectedHighlight: "",
    width: 5,
    isHighlightsActive: false,
    isLinkActive: false,
    isMoveActive: false,

    setWidth(width) {
        set((state) => {
            const updatedHighlightDataSet = Object.fromEntries(
                Object.entries(state.highlightDataSet).map(([pageId, highlights]) => [
                    pageId,
                    highlights.map(h =>
                        ("isDraft" in h && h.isDraft) ? { ...h, width } : h
                    )
                ])
            );

            return {
                ...state,
                width,
                highlights: state.highlights.map(h =>
                    ("isDraft" in h && h.isDraft) ? { ...h, width } : h
                ),
                highlightDataSet: updatedHighlightDataSet
            };
        });
    },
    highlightDataSet: {},
    setSystemDDL(systems) {
        set((state) => ({ ...state, systemDDL: systems }))
    },
    setHighlights(highlight) {
        set((state) => ({ ...state, highlights: highlight }))
    },
    addHighlight(highlight) {
        set((state) => ({ ...state, highlights: [...state.highlights, highlight] }))
    },
    updateHighlight(id, highlight) {
        const updatedHighlight = get().highlights.map((item) => {
            if (item.id === id) {
                return { ...item, ...highlight }
            }
            return { ...item }
        });
        set((state) => ({ ...state, highlights: updatedHighlight }))
    },
    deleteHighlight(id) {
        set((state) => ({ ...state, highlights: state.highlights.filter((item) => item.id !== id) }))
    },
    setIsHighlightsActive: (isActive) => set(() => ({ isHighlightsActive: isActive, isLinkActive: false, isMoveActive: false })),
    setIsLinkActive: (isActive) => set(() => ({ isHighlightsActive: false, isLinkActive: isActive, isMoveActive: false })),
    setIsMoveActive: (isActive) => set(() => ({ isMoveActive: isActive, isHighlightsActive: false, isLinkActive: false })),
    selectedPageId: "",

    setSelectedPageId(id) {
        set((state) => ({
            ...state,
            selectedPageId: id
        }))
    },

    setSelectedHighlight(id) {
        set((state) => ({ ...state, selectedHighlight: id }));
    },

    // Add a highlight to a specific page
    addHighlightToPage(pageId: string, highlight: PdfHighlighterEntity) {
        set((state) => {
            const pageHighlights = state.highlightDataSet[pageId] || [];
            return {
                ...state,
                highlightDataSet: {
                    ...state.highlightDataSet,
                    [pageId]: [...pageHighlights, highlight],
                },
            };
        });
    },

    // Update a highlight in a specific page
    updateHighlightInPage(pageId: string, highlightId: string, data: Partial<PdfHighlighterEntity>) {
        set((state) => {
            const pageHighlights = state.highlightDataSet[pageId] || [];
            const updatedHighlights = pageHighlights.map((h) =>
                h.id === highlightId ? { ...h, ...data } : h
            );
            return {
                ...state,
                highlightDataSet: {
                    ...state.highlightDataSet,
                    [pageId]: updatedHighlights,
                },
            };
        });
    },

    // Remove a highlight from a specific page
    removeHighlightFromPage(pageId: string, highlightId: string) {
        set((state) => {
            const pageHighlights = state.highlightDataSet[pageId] || [];
            return {
                ...state,
                highlightDataSet: {
                    ...state.highlightDataSet,
                    [pageId]: pageHighlights.filter((h) => h.id !== highlightId),
                },
            };
        });
    },

    // Get highlights for a specific page
    getHighlightsByPage(pageId: string): PdfHighlighterEntity[] {
        return get().highlightDataSet[pageId] || [];
    },

    iconCache: new Map(),

    setIconCache(key, icon) {
        set((state) => {
            const newCache = new Map(state.iconCache);
            newCache.set(key, icon);
            return { ...state, iconCache: newCache };
        });
    },

    getIconCache(key) {
        return get().iconCache.get(key);
    },

    removeIconCache(key) {
        set((state) => {
            const newCache = new Map(state.iconCache);
            newCache.delete(key);
            return { ...state, iconCache: newCache };
        });
    },

    clearIconCache() {
        set(() => ({ iconCache: new Map() }));
    },

    setHighlightDataSet(data) {
        set((state) => ({
            ...state,
            highlightDataSet: {
                ...data
            }
        }));
    },

    clearAllState: () => {
        set((state) => ({
            highlightDataSet: {},
            highlights: [],
            isHighlightsActive: false,
            selectedHighlight: "",
            selectedPageId: "",
            isLinkActive: false,
            isMoveActive: false,
            iconCache: new Map()
        }))
    }

}));




interface useHighlightModelProp {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    clearHighlighModelStore: () => void;
}

export const useHighlightModelStore = create<useHighlightModelProp>((set) => ({
    isOpen: false,
    onClose() {
        set((state) => ({ ...state, isOpen: false }))
    },
    onOpen() {
        set((state) => ({ ...state, isOpen: true }))
    },
    clearHighlighModelStore() {
        set((state) => ({ ...state, isOpen: false }))
    },
}));


interface useMergeHighlightModelProp {
    isOpen: boolean;
    overlapHighlights: PdfHighlighterEntity[] | [];
    currentHighlight: PointsEntity[] | [];
    onOpen: () => void;
    onClose: () => void;
    setOverlapHighligh: (overlap: PdfHighlighterEntity[] | [], currentHighlight: PointsEntity[] | []) => void;
    clearUserMergeHighligh: () => void;
}

export const useMergeHighlightModelStore = create<useMergeHighlightModelProp>((set) => ({
    isOpen: false,
    onClose() {
        set((state) => ({ ...state, isOpen: false, overlapHighlights: [], currentHighlight: [] }))
    },
    onOpen() {
        set((state) => ({ ...state, isOpen: true }))
    },
    continuingDraft: false,
    draftStrokeId: "",
    overlapHighlights: [],
    currentHighlight: [],
    setOverlapHighligh(overlap, currentHighlight) {
        set((state) => ({ ...state, currentHighlight, overlapHighlights: overlap }));
    },
    clearUserMergeHighligh() {
        set((state) => ({ ...state, isOpen: false, overlapHighlights: [], currentHighlight: [] }))
    }
}));

interface useSolutionLinkModelProp {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}

export const useSolutionLinkModelStore = create<useSolutionLinkModelProp>((set) => ({
    isOpen: false,
    onClose() {
        set((state) => ({ ...state, isOpen: false }))
    },
    onOpen() {
        set((state) => ({ ...state, isOpen: true }))
    },
}))